import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Copy } from "lucide-react";
import { API_URL } from "../config";

const TABS = [
  ["CI/CD Pipelines", "/integration"],
  ["OTA Deployment", "/ota-deployment"],
  ["Downloads", "/downloads"],
  ["REST API", "/rest-api"],
];

// Every endpoint below exists in the backend today. All artifact endpoints
// need "Authorization: Bearer <token>": a signed-in user's login token or a CI
// credential (ipq_ci_...). A CI credential only reaches the artifact endpoints,
// only artifacts it uploaded itself, and only the Digital IDs it was created for.
const ENDPOINT_GROUPS = [
  {
    name: "Artifact Signing",
    endpoints: [
      { method: "POST", path: "/api/artifact/upload", desc: "Upload an artifact (any file type); returns artifact_id and server-computed hashes" },
      { method: "POST", path: "/api/artifact/generate-manifest", desc: "Create (or return) the stored manifest; required before signing" },
      { method: "POST", path: "/api/artifact/sign", desc: "Sign with a Digital ID you own; repeating the same request returns the existing signature" },
      { method: "POST", path: "/api/artifact/verify", desc: "Re-check the stored artifact against a signature (hash, signature, manifest, timestamp, ledger, certificate)" },
      { method: "POST", path: "/api/artifact/verify-file", desc: "Verify a file you upload (signed or original) against signatures in your account" },
      { method: "GET", path: "/api/artifact/download/{artifact_id}", desc: "Download the signed artifact" },
      { method: "GET", path: "/api/artifact/download/manifest/{artifact_id}", desc: "Download manifest.json (exactly the bytes that were hashed)" },
      { method: "GET", path: "/api/artifact/download/signature/{artifact_id}", desc: "Download the detached signature (.sig, or .json for hybrid)" },
      { method: "GET", path: "/api/artifact/download/public-key/{artifact_id}", desc: "Download the public key (.bin post-quantum, .pem ECDSA, .json hybrid)" },
    ],
  },
  {
    name: "CI Credentials",
    endpoints: [
      { method: "POST", path: "/api/ci-clients", desc: "Create a CI credential (login token required). The token is returned once" },
      { method: "GET", path: "/api/ci-clients", desc: "List your CI credentials (never shows the token)" },
      { method: "PUT", path: "/api/ci-clients/{id}/revoke", desc: "Revoke a credential; it stops working on the next request" },
    ],
  },
  {
    name: "Audit (administrators)",
    endpoints: [
      { method: "GET", path: "/api/audit-events", desc: "Artifact signing, verification, download and credential events (filters: event, status, artifact_id, start_date, end_date)" },
      { method: "GET", path: "/api/audit-logs", desc: "Document signing log" },
    ],
  },
  {
    name: "OTA Deployment",
    endpoints: [
      { method: "GET", path: "/api/ota/device-groups", desc: "List registered device groups (optional ?platform=)" },
      { method: "POST", path: "/api/ota/device-groups", desc: "Register or update a device group" },
      { method: "POST", path: "/api/ota/deploy", desc: "Deploy a signed artifact to a device group" },
      { method: "GET", path: "/api/ota/deployments/{id}", desc: "Get a deployment's recorded status" },
    ],
  },
];

const METHOD_CLASS = {
  POST: "method-post",
  GET: "method-get",
  PUT: "method-post",
};

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable, ignore */
    }
  };
  return (
    <button type="button" className="copy-btn" onClick={handleCopy}>
      <Copy size={12} />
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

export default function RestApi() {
  const navigate = useNavigate();
  const location = useLocation();
  const { artifactId, selectedDigitalId, includeTSA, anchorBlockchain } = location.state || {};
  const [search, setSearch] = useState("");
  const [openGroup, setOpenGroup] = useState("Artifact Signing");

  const filteredGroups = ENDPOINT_GROUPS.map((g) => ({
    ...g,
    endpoints: g.endpoints.filter((e) =>
      (e.path + e.desc).toLowerCase().includes(search.toLowerCase())
    ),
  }));

  const auth = `-H "Authorization: Bearer $IPQSIGN_TOKEN"`;
  const idValue = artifactId || "<artifact_id>";

  const uploadExample = `curl -X POST "${API_URL}/api/artifact/upload" \\
  ${auth} \\
  -F "file=@firmware.bin"`;

  const manifestExample = `curl -X POST "${API_URL}/api/artifact/generate-manifest" \\
  ${auth} \\
  -H "Content-Type: application/json" \\
  -d '{"artifact_id": ${idValue}}'`;

  const signExample = `curl -X POST "${API_URL}/api/artifact/sign" \\
  ${auth} \\
  -H "Content-Type: application/json" \\
  -d '{
    "artifact_id": ${idValue},
    "digital_id": ${selectedDigitalId?.id ?? "<digital_id>"},
    "include_tsa": ${includeTSA ? "true" : "false"},
    "blockchain_anchor": ${anchorBlockchain ? "true" : "false"}
  }'`;

  const signResponseExample = `{
  "success": true,
  "already_signed": false,
  "signature_id": 42,
  "artifact_id": ${idValue},
  "digital_id": ${selectedDigitalId?.id ?? 15},
  "algorithm": "Dilithium3",
  "hash_algorithm": "SHA-256",
  "signed_hash": "…",
  "manifest_sha256": "…",
  "payload_version": 2,
  "signed_at": "2026-10-20T09:15:00Z",
  "signature": "…",
  "public_key": "…"
}`;

  const verifyExample = `curl -X POST "${API_URL}/api/artifact/verify" \\
  ${auth} \\
  -H "Content-Type: application/json" \\
  -d '{"artifact_id": ${idValue}, "signature_id": 42}'

# Publish only if "verification_status" is "VERIFIED".
# The response also lists hash_match, signature_valid, manifest_valid,
# timestamp_valid, blockchain_valid and certificate_valid.`;

  const downloadExample = `curl -o signed_firmware.bin ${auth} \\
  "${API_URL}/api/artifact/download/${idValue}"`;

  const deployExample = `curl -X POST "${API_URL}/api/ota/deploy" \\
  ${auth} \\
  -H "Content-Type: application/json" \\
  -d '{
    "artifact_id": ${idValue},
    "platform": "aws",
    "device_group": "Production Devices",
    "strategy": "Rolling Update",
    "batch_size": "10%",
    "health_check_minutes": "5 minutes",
    "max_retry": "3",
    "rollback_enabled": true,
    "thing_group_arn": "arn:aws:iot:us-east-1:123456789012:thinggroup/Production"
  }'`;

  return (
    <div className="rest-api-page">
      <div className="rest-api-tabs">
        {TABS.map(([label, path]) => (
          <button
            key={label}
            type="button"
            onClick={() => navigate(path, { state: location.state })}
            className={label === "REST API" ? "active" : ""}
          >
            {label}
          </button>
        ))}
      </div>

      <section className="panel overview-panel">
        <h2>REST API</h2>
        <p>
          These are the routes exposed by this backend. Artifact calls need
          <code> Authorization: Bearer &lt;token&gt; </code> — a signed-in user's login token, or a CI
          credential created on the CI/CD page. There is no separate API gateway and no rate limiting yet.
        </p>
      </section>

      <div className="rest-api-grid">
        <aside className="panel endpoint-panel">
          <input
            className="endpoint-search"
            placeholder="Search endpoints…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="endpoint-groups">
            {filteredGroups.map((group) => (
              <div key={group.name}>
                <button
                  type="button"
                  className="endpoint-group-header"
                  onClick={() => setOpenGroup(openGroup === group.name ? "" : group.name)}
                >
                  {group.name}
                  <span className={`chevron ${openGroup === group.name ? "open" : ""}`}>▾</span>
                </button>
                {openGroup === group.name && (
                  <div className="endpoint-list">
                    {group.endpoints.map((e) => (
                      <div className="endpoint-item" key={e.path}>
                        <span className={`method-badge ${METHOD_CLASS[e.method]}`}>{e.method}</span>
                        <div className="endpoint-item-text">
                          <span className="endpoint-item-path">{e.path}</span>
                          <span className="endpoint-item-desc">{e.desc}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </aside>

        <section className="panel detail-panel">
          <h3>Sign an artifact — end to end</h3>
          <p className="detail-desc">
            The sequence below is what the wizard and the CI script both run: upload, generate a
            manifest, sign with a Digital ID, verify, then download. Set <code>IPQSIGN_TOKEN</code> to a
            login token or a CI credential first.
          </p>

          <div className="block-label">1. Upload</div>
          <div className="code-block dark">
            <pre>{uploadExample}</pre>
            <CopyButton text={uploadExample} />
          </div>

          <div className="block-label">2. Generate manifest</div>
          <div className="code-block dark">
            <pre>{manifestExample}</pre>
            <CopyButton text={manifestExample} />
          </div>

          <div className="block-label">3. Sign (requires a real Digital ID)</div>
          <div className="code-block dark">
            <pre>{signExample}</pre>
            <CopyButton text={signExample} />
          </div>

          <div className="block-label">Example response</div>
          <div className="code-block dark">
            <pre>{signResponseExample}</pre>
            <CopyButton text={signResponseExample} />
          </div>

          <div className="block-label">4. Verify</div>
          <div className="code-block dark">
            <pre>{verifyExample}</pre>
            <CopyButton text={verifyExample} />
          </div>

          <div className="block-label">5. Download</div>
          <div className="code-block dark">
            <pre>{downloadExample}</pre>
            <CopyButton text={downloadExample} />
          </div>

          <div className="block-label">6. Deploy over-the-air (optional)</div>
          <p className="detail-desc" style={{ marginTop: -6, marginBottom: 10 }}>
            Only <code>platform: "aws"</code> currently dispatches for real, and only with AWS
            credentials configured on the server — every other platform records the deployment
            and reports back <code>"not_configured"</code>.
          </p>
          <div className="code-block dark">
            <pre>{deployExample}</pre>
            <CopyButton text={deployExample} />
          </div>
        </section>
      </div>

      <style>{`
        .rest-api-page { color: #1A1D29; }

        .rest-api-tabs { display: flex; flex-direction: row; justify-content: flex-start; align-items: center; gap: 28px; width: 100%; border-bottom: 1px solid #E8E9F1; margin-bottom: 20px; }
        .rest-api-tabs button { flex: 0 0 auto; margin: 0; background: none; border: none; border-bottom: 2px solid transparent; padding: 0 0 12px; color: #8B92A8; font-size: 14px; font-weight: 600; cursor: pointer; }
        .rest-api-tabs button.active { color: #5B4FE0; border-bottom-color: #5B4FE0; }

        .panel { border: 1px solid #E8E9F1; border-radius: 12px; padding: 20px; background: #fff; }
        .panel h2 { margin: 0 0 6px; font-size: 18px; }
        .panel h3 { margin: 0 0 4px; font-size: 15px; }

        .overview-panel { margin-bottom: 16px; padding: 16px 18px; }
        .overview-panel > p { margin: 0; color: #8B92A8; font-size: 13px; }

        .rest-api-grid { display: grid; grid-template-columns: 260px 1fr; gap: 16px; align-items: start; }

        .endpoint-panel { padding: 16px; }
        .endpoint-search { width: 100%; box-sizing: border-box; border: 1px solid #E8E9F1; border-radius: 8px; padding: 8px 10px; font-size: 12.5px; margin: 0 0 14px; outline: none; }
        .endpoint-search:focus { border-color: #5B4FE0; }
        .endpoint-groups { display: flex; flex-direction: column; gap: 2px; }
        .endpoint-group-header { width: 100%; display: flex; justify-content: space-between; align-items: center; background: none; border: none; padding: 9px 4px; font-size: 12.5px; font-weight: 600; color: #4B5165; cursor: pointer; border-radius: 6px; }
        .endpoint-group-header:hover { background: #F6F6FB; }
        .chevron { transition: transform 160ms ease; color: #8B92A8; font-size: 11px; }
        .chevron.open { transform: rotate(180deg); }
        .endpoint-list { display: flex; flex-direction: column; gap: 2px; margin-bottom: 6px; }
        .endpoint-item { display: flex; align-items: flex-start; gap: 8px; text-align: left; background: none; border: none; border-radius: 6px; padding: 7px 8px; }
        .endpoint-item-text { display: flex; flex-direction: column; }
        .endpoint-item-path { font-size: 11.5px; font-weight: 600; color: #1A1D29; word-break: break-all; }
        .endpoint-item-desc { font-size: 11px; color: #9AA1B4; }

        .method-badge { font-size: 10px; font-weight: 700; border-radius: 5px; padding: 2px 6px; line-height: 1.4; white-space: nowrap; margin-top: 1px; }
        .method-post { background: #DEFCE9; color: #16A34A; }
        .method-get { background: #E6F0FF; color: #2563EB; }

        .detail-panel { padding: 20px; }
        .detail-desc { font-size: 12.5px; color: #8B92A8; }
        .detail-desc code { background: #F6F6FB; padding: 1px 5px; border-radius: 4px; font-size: 11.5px; }

        .block-label { font-size: 12px; font-weight: 600; color: #4B5165; margin: 16px 0 8px; }
        .block-label:first-of-type { margin-top: 0; }

        .code-block { position: relative; background: #14151F; border-radius: 8px; padding: 14px 44px 14px 14px; }
        .code-block pre { margin: 0; color: #E7E8F3; font-size: 11.5px; font-family: "SFMono-Regular", Consolas, monospace; white-space: pre-wrap; line-height: 1.6; }
        .code-block .copy-btn { position: absolute; top: 10px; right: 10px; color: #C9CBE0; }

        .copy-btn { display: flex; align-items: center; gap: 4px; background: none; border: none; color: #8B92A8; font-size: 11px; cursor: pointer; padding: 2px 4px; }
        .copy-btn:hover { color: #5B4FE0; }

        @media (max-width: 800px) {
          .rest-api-grid { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
}